import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Linkedin, ShieldCheck, Users } from "lucide-react";
import PageShell from "@/components/opsiq/PageShell";
import Seo from "@/components/Seo";

const NATALIE_BIO = [
  "I didn\u2019t come to technology through code. I came to it through people and problems.",
  "My career began in Human Resources and General Management, where I learned how organisations really run: where processes break down, people get stuck, and good intentions turn into lost time and money. That foundation gave me a practical understanding of business operations and the human side of organisational growth.",
  "My introduction to technology came through Go-To-Market leadership in the fintech industry, eventually leading to my role as Vice President responsible for the USA, Canada and the Caribbean. Leading across those markets was a turning point. Working closely with product and engineering teams, I realised that technology is not about the software itself — it is about the problem it solves, the inefficiency it eliminates, and the difference it makes to the people and businesses using it.",
  "From there, I taught myself to build. I moved from an HR and marketing background into designing and developing applications and software, turning business challenges into functional digital solutions. Today, through OpsIQ, I combine business strategy, people management and hands-on technology experience to help organisations identify operational gaps and close them with practical, purpose-built tools.",
  "The journey continues to be an eye-opener. Each project brings something new to learn, and some solutions are transformative. My starting point never changes: I understand the business first, then build what it needs.",
  "I am currently pursuing an MBA in FinTech Management at Abertay University, further strengthening the bridge between business strategy and technology innovation.",
  "At OpsIQ, we believe technology should serve business — and that belief drives everything we build.",
];

const TEAM = [
  {
    key: "natalie",
    name: "Natalie Annan",
    role: "Principal Consultant",
    kicker: "From People and Business Management to Technology-Driven Solutions",
    photo: "/team-natalie-annan.jpg",
    alt: "Natalie Annan, Principal Consultant at OpsIQ",
    Icon: Users,
    accent: "#D9FF3C",
    bio: NATALIE_BIO,
  },
  {
    key: "angel-lyric",
    name: "Angel-Lyric Appiah Koomson",
    role: "Compliance Analyst",
    kicker: "Business compliance, due diligence and financial-crime prevention",
    photo: "/team-angel-lyric.jpg",
    alt: "Angel-Lyric Appiah Koomson, Compliance Analyst at OpsIQ",
    Icon: ShieldCheck,
    accent: "#B8E024",
    bio: [
      "Angel-Lyric Appiah Koomson is a compliance professional with practical experience in business compliance, due diligence, customer verification, claims investigations and financial crime prevention.",
      "She has worked as a Consultant to the Official Liquidator through ABG Consult Chartered Accountants & Business Advisers, supporting the resolution of fund management companies. In this role, she has reviewed and verified over 5,000 Proof of Debt forms and supporting documents, conducted Know Your Customer (KYC) checks, investigated claims and supported the identification and prevention of fraudulent activities. Her experience also includes processing customer claims, preparing reports and ensuring accuracy and transparency in compliance-related procedures.",
      "Angel holds professional certifications from the International Compliance Association (ICA) in Anti-Money Laundering, KYC and Customer Due Diligence, and Managing Sanctions Risk. She also holds a Diploma in Public Administration from the University of Ghana Business School and is pursuing a BSc in Business Administration at the Ghana Institute of Management and Public Administration (GIMPA).",
      "At OpsIQ, she contributes compliance knowledge and analytical support to the development of practical solutions that help organisations strengthen their compliance processes, manage risk and improve operational accountability.",
      "She brings a detail-oriented, investigative and risk-conscious approach to her work, with a strong commitment to promoting organisational integrity and helping businesses meet their compliance obligations.",
    ],
  },
];

export default function TeamPage() {
  return (
    <>
      <Seo
        title="The OpsIQ Team — Natalie Annan & Angel-Lyric Appiah Koomson"
        description="Meet the people behind OpsIQ. Natalie Annan, Principal Consultant — business strategy meets hands-on technology. Angel-Lyric Appiah Koomson, Compliance Analyst — governance, risk and compliance."
        path="/team"
        image="/og-image.jpg?v=2"
        imageAlt="The OpsIQ team — Natalie Annan and Angel-Lyric Appiah Koomson"
      />
      <PageShell
        testid="team-page"
        eyebrow="Team"
        title={<>The people behind <span className="italic">OpsIQ.</span></>}
        subtitle="Small, senior, and hands-on. Every engagement is led by the people you see on this page."
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 pb-20 md:pb-28">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-10">
            {TEAM.map((m, i) => (
              <motion.article
                key={m.key}
                initial={{ opacity: 0, y: 32 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`team-${m.key}`}
                className="border border-white/10 bg-[#142645]/60 backdrop-blur-sm overflow-hidden"
              >
                {/* Photo header */}
                <div className="relative aspect-[4/5] md:aspect-[5/6] lg:aspect-[4/5] overflow-hidden bg-[#0E1830]">
                  {m.photo ? (
                    <img
                      src={m.photo}
                      alt={m.alt}
                      className="w-full h-full object-cover object-top"
                      loading="lazy"
                    />
                  ) : (
                    <div
                      data-testid={`team-${m.key}-placeholder-photo`}
                      className="w-full h-full flex flex-col items-center justify-center gap-4 bg-gradient-to-b from-[#0E1830] to-[#142645]"
                    >
                      <div className="h-32 w-32 md:h-40 md:w-40 rounded-full bg-[#D9FF3C]/10 border border-[#D9FF3C]/25 flex items-center justify-center">
                        <span className="font-editorial text-4xl md:text-5xl text-[#D9FF3C]">
                          {m.name.split(" ").map((p) => p[0]).slice(0, 2).join("")}
                        </span>
                      </div>
                      <div className="text-[10px] font-plex-mono uppercase tracking-[0.22em] text-[#F1ECE0]/45">
                        Headshot coming soon
                      </div>
                    </div>
                  )}
                  <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-[#142645] to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                    <div className="text-[10px] font-plex-mono uppercase tracking-[0.22em] mb-2" style={{ color: m.accent }}>
                      {m.role}
                    </div>
                    <h2 className="font-editorial text-3xl md:text-4xl lg:text-5xl leading-tight tracking-tight text-[#F1ECE0]">
                      {m.name}
                    </h2>
                    <div className="mt-2 text-sm md:text-base italic text-[#F1ECE0]/75 font-editorial">
                      {m.kicker}
                    </div>
                  </div>
                </div>

                {/* Bio */}
                <div className="p-6 md:p-8 lg:p-10 space-y-5">
                  {m.bio.map((para, j) => (
                    <p
                      key={j}
                      className={
                        j === 0
                          ? "text-lg md:text-xl font-editorial text-[#F1ECE0] leading-snug"
                          : "text-[15px] leading-relaxed text-[#F1ECE0]/75"
                      }
                    >
                      {para}
                    </p>
                  ))}
                  {m.bioNote && (
                    <p className="text-[10px] font-plex-mono uppercase tracking-[0.22em] text-[#D9FF3C]/70">
                      {m.bioNote}
                    </p>
                  )}
                </div>
              </motion.article>
            ))}
          </div>

          {/* Footer CTA */}
          <div className="mt-16 md:mt-24 border-t border-white/10 pt-10 md:pt-14 flex flex-col md:flex-row md:items-center gap-6 justify-between">
            <div className="max-w-xl">
              <div className="text-[10px] font-plex-mono uppercase tracking-[0.22em] text-[#D9FF3C] mb-3">
                Work with us
              </div>
              <h3 className="font-editorial text-3xl md:text-4xl leading-tight tracking-tight text-[#F1ECE0]">
                Your first conversation is with us directly.
              </h3>
              <p className="mt-3 text-[#F1ECE0]/70">
                No juniors, no hand-offs. Natalie leads every engagement personally, with Angel-Lyric on governance and
                risk from day one.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to="/book"
                className="inline-flex items-center justify-center gap-2 px-6 py-4 bg-[#D9FF3C] text-[#142645] text-xs uppercase tracking-[0.22em] font-semibold hover:bg-[#E4FF66] transition-colors"
              >
                Book a consultation <ArrowUpRight size={16} />
              </Link>
              <a
                href="https://wa.me/233546080065?text=Hello%20OpsIQ%20%E2%80%94%20I%27d%20like%20to%20speak%20with%20Natalie%20about%20a%20project."
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center gap-2 px-6 py-4 border border-[#D9FF3C]/50 text-[#D9FF3C] text-xs uppercase tracking-[0.22em] font-semibold hover:bg-[#D9FF3C] hover:text-[#142645] transition-colors"
              >
                WhatsApp us
              </a>
            </div>
          </div>
        </div>
      </PageShell>
    </>
  );
}
